import type {
  ThemeOptions,
  ComponentsProps,
  ComponentsOverrides,
  ComponentsVariants
} from '@mui/material/styles';
import type { Theme } from '@ui/ThemeRegistry/theme.types';

const defaultProps: ComponentsProps['NavigationItem'] = {};

const styleOverrides: ComponentsOverrides<Theme>['NavigationItem'] = {
  root: ({ theme }) => ({
    'position': 'relative',

    // Disable hover effects for group variants
    '&[data-variant="group"]': {
      pointerEvents: 'none'
    },

    '&:hover, &:focus-within': {
      '& > [class*=NavItemSubMenu]': {
        maxHeight: '1000px',
        opacity: 1,
        visibility: 'visible',
        overflow: 'visible'
      }
    }
  }),

  navItemSubMenu: ({ theme }) => ({
    transition: 'max-height 0.3s ease, opacity 0.3s ease, visibility 0.3s',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(4),
    zIndex: 1000,

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
      boxShadow: theme.shadows[2],
      borderRadius: theme.shape.borderRadius
    }
  }),

  navItemSubMenuItem: ({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',
    'width': 'auto',
    'padding': 0,

    // Style for column headers (AI Agents, Web Development)
    '& > [data-variant="group"]': {
      fontWeight: 600,
      fontSize: '1.1rem',
      marginBottom: theme.spacing(2),
      color: theme.palette.text.primary
    },

    // Style for sub-navigation items
    '& > div > div': {
      marginBottom: theme.spacing(1)
    },

    // Prevent additional hover effects on column headers
    '&:hover, &:focus-within': {
      '& > [data-variant="group"]': {
        // No additional hover effects
      }
    },

    [theme.breakpoints.up('md')]: {
      width: 'auto',
      minWidth: '300px'
    }
  }),

  link: ({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',

    // Style for sub-navigation items
    '& > div': {
      marginBottom: theme.spacing(1)
    },

    // Prevent additional hover effects
    '&:hover, &:focus-within': {
      // No additional hover effects
    }
  }),

  featuredNavigationItem: ({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'row',
    'alignItems': 'flex-start',
    'gap': theme.spacing(2),
    'padding': theme.spacing(2),
    'borderRadius': theme.spacing(1),
    'transition': 'background-color 0.2s ease',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)'
    }
  }),

  featuredImage: ({ theme }) => ({
    'flexShrink': 0,
    'width': '48px',
    'height': '48px',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    }
  }),

  featuredSummary: ({ theme }) => ({
    'fontSize': '0.875rem',
    'color': theme.palette.text.secondary,
    'maxWidth': '300px',
    'whiteSpace': 'normal',
    'overflow': 'visible',
    'textOverflow': 'ellipsis',
    'display': '-webkit-box',
    'WebkitLineClamp': 2,
    'WebkitBoxOrient': 'vertical',
    'lineHeight': '1.4',

    '& p': {
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  })
};

const createVariants = (_theme: Theme): ComponentsVariants['NavigationItem'] => [];

export const navigationItemTheme = (theme: Theme): ThemeOptions => ({
  components: {
    NavigationItem: {
      defaultProps,
      styleOverrides: styleOverrides as ComponentsOverrides<Theme>['NavigationItem'],
      variants: createVariants(theme)
    }
  }
});

export default navigationItemTheme;
